"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getAuthMe,
  getEntitlements,
  getMyAccess,
  type AuthMe,
  type Entitlements,
  type MyAccess,
  type PermissionAction,
} from "@/lib/api";

// Mirrors backend/app/domain/permission_matrix.py's _ACTIONS_BY_LEVEL — the
// KEY's numbers are NOT in intuitive order (2 is the most-permissive level).
const ACTIONS_BY_LEVEL: Record<number, ReadonlySet<PermissionAction>> = {
  2: new Set(["read", "write", "add", "delete"]),
  3: new Set(["read", "write", "add"]),
  4: new Set(["read", "write"]),
  5: new Set(["read"]),
  0: new Set([]),
};

function accessKey(module: string, submodule: string | null, fn: string): string {
  return `${module}:${submodule ?? ""}:${fn}`;
}

type AuthContextValue = {
  me: AuthMe | null;
  loading: boolean;
  entitlements: Entitlements | null;
  /** Whether the company may use a paid module. Fail-open: true when
   * enforcement is off or entitlements are unknown (fetch failed/loading). */
  hasModule: (module: string) => boolean;
  myAccess: MyAccess | null;
  /** Effective access level (0/2/3/4/5) for a permission-matrix cell.
   * Fail-CLOSED (0) when unknown — the matrix is enforced backend-side, so
   * the UI should never offer an action the server will 403 on. */
  getAccessLevel: (module: string, submodule: string | null, fn: string) => number;
  canRead: (module: string, submodule: string | null, fn: string) => boolean;
  canWrite: (module: string, submodule: string | null, fn: string) => boolean;
  canAdd: (module: string, submodule: string | null, fn: string) => boolean;
  canDelete: (module: string, submodule: string | null, fn: string) => boolean;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<AuthMe | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [myAccess, setMyAccess] = useState<MyAccess | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Always try /auth/me — backend returns the dev user when AUTH_DISABLED=true
      // even without a token. Only skip if there's no token AND the call fails.
      const data = await getAuthMe();
      setMe(data);
      // Entitlements are a staff-only endpoint; external/portal users never see
      // gated modules anyway, so skip the call (it would 403) for them.
      if (data.account_kind === "craydl_staff") {
        try {
          setEntitlements(await getEntitlements());
        } catch {
          setEntitlements(null); // fail-open
        }
      } else {
        setEntitlements(null);
      }
      // Permission matrix applies to every account, internal and external.
      try {
        setMyAccess(await getMyAccess());
      } catch {
        setMyAccess(null); // fail-closed — see getAccessLevel below
      }
    } catch {
      setMe(null);
      setEntitlements(null);
      setMyAccess(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasModule = useCallback(
    (module: string) =>
      !entitlements?.enforced || !!entitlements?.modules?.includes(module),
    [entitlements],
  );

  const getAccessLevel = useCallback(
    (module: string, submodule: string | null, fn: string) =>
      myAccess?.access[accessKey(module, submodule, fn)]?.level ?? 0,
    [myAccess],
  );
  const actionAllowed = useCallback(
    (module: string, submodule: string | null, fn: string, action: PermissionAction) =>
      ACTIONS_BY_LEVEL[getAccessLevel(module, submodule, fn)]?.has(action) ?? false,
    [getAccessLevel],
  );
  const canRead = useCallback(
    (module: string, submodule: string | null, fn: string) =>
      actionAllowed(module, submodule, fn, "read"),
    [actionAllowed],
  );
  const canWrite = useCallback(
    (module: string, submodule: string | null, fn: string) =>
      actionAllowed(module, submodule, fn, "write"),
    [actionAllowed],
  );
  const canAdd = useCallback(
    (module: string, submodule: string | null, fn: string) =>
      actionAllowed(module, submodule, fn, "add"),
    [actionAllowed],
  );
  const canDelete = useCallback(
    (module: string, submodule: string | null, fn: string) =>
      actionAllowed(module, submodule, fn, "delete"),
    [actionAllowed],
  );

  const value = useMemo(
    () => ({
      me,
      loading,
      entitlements,
      hasModule,
      myAccess,
      getAccessLevel,
      canRead,
      canWrite,
      canAdd,
      canDelete,
      refresh,
    }),
    [
      me,
      loading,
      entitlements,
      hasModule,
      myAccess,
      getAccessLevel,
      canRead,
      canWrite,
      canAdd,
      canDelete,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function isExternalPortalUser(me: AuthMe | null): boolean {
  return me?.account_kind === "external";
}

/** Invited vendor (outside company bidding on a project). A subset of
 * external portal users, routed to the dedicated vendor portal. */
export function isExternalVendorUser(me: AuthMe | null): boolean {
  return me?.account_kind === "external" && me?.role === "vendor";
}

export function isStandardStaff(me: AuthMe | null): boolean {
  if (!me || me.is_super_admin) return false;
  return me.account_kind === "craydl_staff" && me.tenant_role === "standard";
}

export function isAdminDashboard(me: AuthMe | null): boolean {
  if (!me) return false;
  if (me.is_super_admin) return true;
  return me.account_kind === "craydl_staff" && me.tenant_role === "admin";
}

/** System super-admin (CRAYDL system owner). Gates the Super Admin panel.
 * Restricted to @craydl.com accounts — mirrors the backend
 * `require_super_admin` check, which enforces the same domain rule. */
export function isSuperAdmin(me: AuthMe | null): boolean {
  if (!me?.is_super_admin) return false;
  return (me.email ?? "").trim().toLowerCase().endsWith("@craydl.com");
}

/** Internal Craydl staff (admin or standard), not external portal users. */
export function isInternalStaff(me: AuthMe | null): boolean {
  if (!me) return false;
  return me.account_kind === "craydl_staff" && me.tenant_role !== "external";
}

/** Company admins + PMs — the only roles that see the Financials area
 * (AR/AP). PMs see all jobs' financials (company-role gate, not job
 * membership). Mirrors the backend `require_finance_user` check. */
export function canSeeFinancials(me: AuthMe | null): boolean {
  if (!me) return false;
  if (isAdminDashboard(me)) return true;
  return me.account_kind === "craydl_staff" && me.role === "pm";
}

/** Engineering dashboard: a per-user grant (independent of role), not a
 * role gate — today only Phil holds it. Mirrors the backend
 * `require_engineering_dashboard_access` check. */
export function canViewEngineeringDashboard(me: AuthMe | null): boolean {
  return !!me?.can_view_engineering_dashboard;
}
